import React, {
    Dispatch,
    ReactNode,
    SetStateAction,
    createContext,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react"
import {Tab} from "semantic-ui-react"
import {I18nIndividual as GedcomIndividual} from "../model/gedcom-individual"
import {I18nFamily as GedcomFamily} from "../model/gedcom-family"
import {CreatorDataContext} from "./creator-data-context"
import {restoredSession, maxId} from "../utils/creator-utils"
import {useLocation} from "react-router"
import {CreatorPanes} from "./creator-tabs"
import {useCommonCallbacks} from "./creator-callbacks"
import {useFamiliesAnnounce} from "./families-announce"
import {useSessionStorage} from "./creator-storage"
import "../styles/creator.css"


export const startId = -1  // Start from I0 and F0
export const maxRowLimit = 9999

export interface EventHandlers {
    switchTab?: (targetRowId: string) => void
    setIndividualsData?: Dispatch<SetStateAction<GedcomIndividual[]>>
    setFamiliesData?: Dispatch<SetStateAction<GedcomFamily[]>>
    onChange?: (rowIndex: number, field: keyof GedcomFamily | keyof GedcomIndividual, value: any) => void
    startFamily?: (fatherId: string, motherId: string) => void
    setError?: Dispatch<SetStateAction<ReactNode>>
    setUnsavedChanges?: Dispatch<SetStateAction<boolean>>
    setLoading?: Dispatch<SetStateAction<boolean>>
    setAllowConvert?: Dispatch<SetStateAction<boolean>>
    setContactModalOpen?: Dispatch<SetStateAction<boolean>>
}

export const CreatorContext = createContext(new CreatorDataContext())
export function CreatorTabs({ eventHandlers }: { eventHandlers: EventHandlers }) {
    const location = useLocation()
    const from = location.state?.from ?? null

    const menuRef = useRef<HTMLDivElement>(null)
    const [activeTab, setActiveTab] = useState(0)  // Active tab tracking

    const { creatorDataRef } = useContext(CreatorContext)
    const [allDataLoaded, setAllDataLoaded] = useState(false)

    const lastIndividualId = useRef(startId)
    const [individualsData, setIndividualsData] = useState<GedcomIndividual[]>(location.state?.individualsData ?? [])

    const lastFamilyId = useRef(startId)
    const [familiesData, setFamiliesData] = useState<GedcomFamily[]>(location.state?.familiesData ?? [])

    /**
     * Trigger on changes of compound key id-enabled-sex.
     */
    function hashValidIndividuals(individuals: GedcomIndividual[]) {
        return individuals.map(i => `${i.id}-${i.enabled}-${i.sex}`).join("|")
    }

    /**
     * At least 2 individuals of different sex.
     */
    const familiesEnabled = useMemo(() => new Set(individualsData.filter(indi => indi.id && indi.sex).map(indi => indi.sex)).size >= 2,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hashValidIndividuals(individualsData)]), minimumIndividuals = familiesEnabled

    const minimumFamilies = useMemo(() => familiesData.some(f => f.id && f.enabled && f.husbandId && f.wifeId), [familiesData])
    const familiesAnnounceDone = useRef(false)
    const dismissFamiliesAnnounce = useFamiliesAnnounce(from, familiesEnabled, restoredSession().restoredIndividuals || restoredSession().restoredFamilies)

    const { switchTab, startFamily } = useCommonCallbacks(lastFamilyId, setFamiliesData, setActiveTab, dismissFamiliesAnnounce)
    useSessionStorage(from, lastIndividualId, individualsData, setIndividualsData, lastFamilyId, familiesData, setFamiliesData)

    /**
     * Data snapshot for common usage.
     */
    useEffect(() => {
        creatorDataRef.current = { individuals: individualsData, families: familiesData }
    }, [creatorDataRef, individualsData, familiesData])

    /**
     * Active tab.
     */
    useEffect(() => {document.body.dataset.activeTab = String(activeTab)}, [activeTab])

    /**
     * Broadcast that tables finished rendering.
     */
    useEffect(() => {
        if (!allDataLoaded) requestAnimationFrame(() => setAllDataLoaded(true))
    }, [individualsData, familiesData, allDataLoaded])

    /**
     * Update counters with data loaded from /view.
     */
    useEffect(() => {
        if (individualsData.length > 0) lastIndividualId.current = maxId(individualsData)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [individualsData.length])
    useEffect(() => {
        if (familiesData.length > 0) lastFamilyId.current = maxId(familiesData)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [familiesData.length])

    /**
     * Broadcast that the conversion can be enabled.
     */
    useEffect(() => {
        if (allDataLoaded) {
            eventHandlers.setLoading!(false)
            console.log("Data loaded in Creator")
            eventHandlers.setAllowConvert?.(minimumIndividuals && minimumFamilies)
            if (!from)
                eventHandlers.setUnsavedChanges!(minimumIndividuals && minimumFamilies)
        }
    }, [from, allDataLoaded, minimumIndividuals, minimumFamilies, eventHandlers])

/* ------------------------------------------------------------------------------------------------------------------ */
/* CREATOR TABS                                                                                                       */
/* ------------------------------------------------------------------------------------------------------------------ */

    return (
        <Tab
            menu={{ ref: menuRef, className: "creator-tabs", attached: "top", secondary: false }}
            activeIndex={activeTab}
            data-active-tab={activeTab}
            renderActiveOnly={false}
            onTabChange={(_, tabProps) => setActiveTab(tabProps.activeIndex as number)}
            panes={CreatorPanes({
                activeTab,
                from,
                lastIndividualId,
                individualsData,
                setIndividualsData,
                lastFamilyId,
                familiesData,
                setFamiliesData,
                familiesEnabled,
                startFamily,
                switchTab,
                familiesAnnounceDone,
                dismissFamiliesAnnounce,
                setUnsavedChanges: eventHandlers.setUnsavedChanges!,
                setContactModalOpen: eventHandlers.setContactModalOpen!
            })}
        />
    )
}